import { useState } from "react";
import { Crown, FileDown, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { EVENT_CONFIG, API, fmtUSD } from "@/config/eventConfig";
import { SectionHeading, Reveal, GoldButton, useDonate } from "./shared";

const Sponsorship = () => {
  const { openDonate } = useDonate();
  const { sponsorshipTiers, urls } = EVENT_CONFIG;
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (kind, presetMessage) => {
    if (!form.name || !form.email) {
      toast.error("Add your name and email so the S4K team can reach you.");
      document.getElementById("sponsor-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, { kind, ...form, message: presetMessage || form.message });
      toast.success("Received — the Styles 4 Kidz team will be in touch.");
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch {
      toast.error("Something went wrong — please email info@styles4kidz.org directly.");
    } finally {
      setSending(false);
    }
  };

  const downloadDeck = () => {
    if (urls.sponsorshipDeck) {
      window.open(urls.sponsorshipDeck, "_blank", "noopener,noreferrer");
    } else {
      submit("deck", "Please send the Building Crowns 4 The Future sponsorship deck.");
    }
  };

  return (
    <section id="sponsor" data-testid="sponsor-section" className="relative py-24 sm:py-32 bg-ink grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-14">
        <SectionHeading
          chapter="08"
          eyebrow="Corporate & Major Gifts"
          title={
            <>
              DON'T JUST ATTEND.
              <br />
              <span className="gold-shimmer">BUILD THE FUTURE.</span>
            </>
          }
          sub="Official Crown Partnership levels published by Styles 4 Kidz — secure your package on the official event page, or request details below."
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 w-full">
          {sponsorshipTiers.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.07}>
              <div
                data-testid={`sponsorship-tier-${t.id}`}
                className="h-full rounded-3xl bg-gradient-to-b from-oxblood to-coal gold-stroke p-8 flex flex-col gap-4 text-center hover:-translate-y-1.5 transition-transform duration-300"
              >
                <Crown size={22} className="text-gold mx-auto" />
                <h3 className="font-serif text-xl text-ivory">{t.name}</h3>
                <div className="font-serif text-3xl gold-text">
                  {fmtUSD(t.amount)}
                  {t.suffix || ""}
                </div>
                <p className="text-cream/60 text-xs leading-relaxed flex-1">{t.note}</p>
                <a
                  data-testid={`sponsorship-tier-${t.id}-button`}
                  href={urls.buyTickets}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block py-3 rounded-full border border-champagne/50 text-champagne text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-champagne/10 transition-colors"
                >
                  Become a Partner
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="w-full max-w-3xl">
          <div id="sponsor-form" data-testid="sponsor-inquiry-form" className="rounded-3xl bg-coal/80 gold-stroke p-7 sm:p-10 grain">
            <h3 className="font-serif text-2xl text-ivory text-center">Request Sponsorship Information</h3>
            <div className="mt-7 grid sm:grid-cols-2 gap-4">
              <input
                data-testid="sponsor-form-name"
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                className="bg-ink/60 border border-white/10 rounded-xl px-5 py-3.5 text-ivory placeholder:text-cream/40 focus:outline-none focus:border-champagne/60"
              />
              <input
                data-testid="sponsor-form-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Email"
                className="bg-ink/60 border border-white/10 rounded-xl px-5 py-3.5 text-ivory placeholder:text-cream/40 focus:outline-none focus:border-champagne/60"
              />
              <input
                data-testid="sponsor-form-organization"
                value={form.organization}
                onChange={set("organization")}
                placeholder="Company / organization (optional)"
                className="sm:col-span-2 bg-ink/60 border border-white/10 rounded-xl px-5 py-3.5 text-ivory placeholder:text-cream/40 focus:outline-none focus:border-champagne/60"
              />
              <textarea
                data-testid="sponsor-form-message"
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us how you'd like to build crowns…"
                rows={3}
                className="sm:col-span-2 bg-ink/60 border border-white/10 rounded-xl px-5 py-3.5 text-ivory placeholder:text-cream/40 focus:outline-none focus:border-champagne/60 resize-none"
              />
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <GoldButton
                testid="sponsor-form-submit-button"
                onClick={() => submit("sponsorship")}
                className="flex-1"
              >
                <Send size={14} /> {sending ? "Sending…" : "Request Info"}
              </GoldButton>
              <GoldButton testid="sponsor-deck-button" variant="outline" onClick={downloadDeck} className="flex-1">
                <FileDown size={14} /> Download Sponsorship Deck
              </GoldButton>
              <GoldButton
                testid="sponsor-major-gift-button"
                variant="wine"
                onClick={() => openDonate(10000)}
                className="flex-1"
              >
                <Crown size={14} /> Make a Major Gift
              </GoldButton>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Sponsorship;
